/**
 * Content Hub "Download": choose between the web version (the WebP / MP4 copy the hub shows, pictures up to
 * 1600px — small and fast for social and web) and the originals (the files as the creators uploaded them —
 * full resolution, original format, larger). The last choice is remembered on this device. While files are
 * saved the dialog shows "Downloading n of N".
 */
import { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { cn } from './ui/utils';

export type ContentHubDownloadVersion = 'web' | 'original';

export const DOWNLOAD_VERSION_STORAGE_KEY = 'beatly.contentHub.downloadVersion';

// eslint-disable-next-line react-refresh/only-export-components -- tiny storage helper, unit-tested with the dialog
export function loadDownloadVersion(): ContentHubDownloadVersion {
  try {
    return localStorage.getItem(DOWNLOAD_VERSION_STORAGE_KEY) === 'original' ? 'original' : 'web';
  } catch {
    return 'web';
  }
}

function saveDownloadVersion(v: ContentHubDownloadVersion) {
  try {
    localStorage.setItem(DOWNLOAD_VERSION_STORAGE_KEY, v);
  } catch {
    // Private mode / blocked storage: the choice just isn't remembered.
  }
}

const OPTIONS: { id: ContentHubDownloadVersion; title: string; format: string; body: string }[] = [
  {
    id: 'web',
    title: 'Web version',
    format: 'WebP images up to 1600px · MP4 videos',
    body: 'Small files that download fast. Best for social media, websites and digital ads.',
  },
  {
    id: 'original',
    title: 'Originals',
    format: 'Full resolution, in the file format the creator uploaded (often JPG, PNG, HEIC or MOV)',
    body: 'Larger files. Best for print, editing and large formats.',
  },
];

export function ContentHubDownloadDialog({ open, count, busy, progress = 0, onOpenChange, onDownload }: {
  open: boolean;
  /** How many files the download covers. */
  count: number;
  busy: boolean;
  /** Files saved so far while busy. */
  progress?: number;
  onOpenChange: (open: boolean) => void;
  onDownload: (version: ContentHubDownloadVersion) => void;
}) {
  const [version, setVersion] = useState<ContentHubDownloadVersion>(loadDownloadVersion);

  useEffect(() => {
    if (open) setVersion(loadDownloadVersion());
  }, [open]);

  const confirm = () => {
    saveDownloadVersion(version);
    onDownload(version);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-[480px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
        <DialogHeader className="px-6 pt-6 pb-1">
          <DialogTitle className="text-[#101828] text-[18px]" style={{ fontWeight: 700 }}>
            Download {count === 1 ? '1 file' : `${count} files`}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#667085]" aria-live="polite">
            {busy ? `Downloading ${Math.min(progress + 1, count)} of ${count}…` : 'Which version do you need?'}
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          aria-label="Download version"
          value={version}
          onValueChange={(v) => setVersion(v as ContentHubDownloadVersion)}
          disabled={busy}
          className="gap-2 px-6 pt-3"
        >
          {OPTIONS.map((o) => {
            const selected = version === o.id;
            return (
              <label
                key={o.id}
                htmlFor={`contenthub-download-${o.id}`}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
                  selected ? 'border-[#be43ff] bg-[#faf5ff]' : 'border-[#eaecf0] bg-white hover:bg-[#f9fafb]',
                )}
              >
                <RadioGroupItem id={`contenthub-download-${o.id}`} value={o.id} className="mt-0.5 border-[#d0d5dd] data-[state=checked]:border-[#be43ff] [&_svg]:fill-[#be43ff]" />
                <span className="min-w-0">
                  <span className="block text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>{o.title}</span>
                  <span className="block text-[12.5px] text-[#475467]">{o.format}</span>
                  <span className="mt-0.5 block text-[12.5px] text-[#667085]">{o.body}</span>
                </span>
              </label>
            );
          })}
        </RadioGroup>

        <DialogFooter className="px-6 pb-6 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={busy} className="bg-purple-600 text-white hover:bg-purple-700">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> : <Download className="mr-1.5 h-4 w-4" aria-hidden />}
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
